<template>
    <AppLayout>
        <div class="max-w-7xl mx-auto px-4 py-6">
            <!-- Page Title -->
            <div class="mb-6">
                <h1 class="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
                    {{ $tc('LBL_CHAT', 2) }}
                </h1>
                <!-- Breadcrumb -->
                <nav
                    class="text-sm text-gray-500 mb-6"
                    aria-label="Breadcrumb"
                >
                    <ol class="flex items-center space-x-2">
                        <li class="flex items-center">
                            <inertia-link
                                :href="route('landing')"
                                class="text-green-600 hover:underline"
                            >
                                {{ $t('LBL_HOME') }}
                            </inertia-link>
                            <svg
                                class="w-3 h-3 mx-2 text-green-400"
                                fill="currentColor"
                                viewBox="0 0 320 512"
                            >
                                <path
                                    d="M285.476 272.971L91.132 467.314c-9.373 9.373-24.569 9.373-33.941 0l-22.667-22.667c-9.357-9.357-9.375-24.522-.04-33.901L188.505 256 34.484 101.255c-9.335-9.379-9.317-24.544.04-33.901l22.667-22.667c9.373-9.373 24.569-9.373 33.941 0L285.475 239.03c9.373 9.372 9.373 24.568.001 33.941z"
                                />
                            </svg>
                        </li>
                        <li class="text-gray-700">
                            {{ $tc('LBL_MESSAGE', 1) }}
                        </li>
                    </ol>
                </nav>
            </div>
            <!-- breadcrumb end -->

            <div class="flex md:hidden justify-between items-center mb-6">
                <div class="text-sm">People You Talk To</div>

                <t-button
                    class="px-3 py-1 text-sm bg-white border rounded cursor-pointer md:hidden"
                    variant="outline"
                    @click="openChatList = true"
                >
                    Messaging
                </t-button>

                <!-- Chat list for smaller screen  -->
                <div
                    class="fixed inset-0 z-20 overflow-y-auto duration-300 ease-out transform bg-white md:hidden"
                    :class="openChatList ? 'translate-y-0' : 'translate-y-full'"
                >
                    <div class="flex items-center justify-between h-12 px-3 py-2 shadow">
                        <p class="font-semibold text-xl">Messages</p>
                        <div @click="closeChatList()">
                            <CloseIcon class="w-6 fill-current" />
                        </div>
                    </div>
                    <div class="px-3 py-2">
                        <ChatList
                            v-model="search"
                            :user-list="chatUserList"
                            :is-loading="loading"
                            :is-current-recipient="isCurrentRecipient"
                            @onUserSelect="closeChatList"
                        />
                    </div>
                </div>
            </div>

            <div class="flex gap-2 h-3/4">
                <!-- chat list -->
                <div class="flex-shrink-0 hidden w-1/3 px-4 py-2 bg-white rounded shadow md:block">
                    <ChatList
                        v-model="search"
                        :user-list="chatUserList"
                        :is-loading="loading"
                        :is-current-recipient="isCurrentRecipient"
                        @onUserSelect="changeCurrentUser"
                    />
                </div>

                <!-- currentMessages -->
                <div class="flex flex-col flex-grow bg-white rounded shadow overflow-hidden">
                    <div
                        v-if="currentUser"
                        class="px-4 py-3 font-semibold border-b md:text-lg"
                    >
                        {{ currentUser.name }}
                    </div>

                    <!-- Scrollable Messages -->
                    <div
                        class="overflow-y-auto px-3 py-2"
                        id="chat-messages"
                        ref="chatmessagesref"
                        style="height: calc(100vh - 300px)"
                    >
                        <ChatMessages :messages="currentMessages" />
                    </div>
                    <div class="h-20 px-3 py-2">
                        <ChatForm
                            :disabled="!!!currentUser"
                            :user="currentUser || {}"
                            @messagesent="addMessage"
                        />
                    </div>
                </div>
            </div>
        </div>
    </AppLayout>
</template>

<script>
    import throttle from 'lodash/throttle';
    import AppLayout from '../../Layouts/AppLayout.vue';
    import ChatMessages from '../../Components/ChatMessages.vue';
    import ChatForm from '../../Components/ChatForm.vue';
    import CloseIcon from '../../Components/Icons/CloseIcon.vue';
    import ChatList from '../../Components/ChatList.vue';

    export default {
        name: 'ChatIndex',

        components: {
            AppLayout,
            ChatMessages,
            ChatForm,
            CloseIcon,
            ChatList,
        },

        props: {
            users: {
                type: Array,
                required: true,
            },

            recipient: {
                type: Object,
                default: null,
            },
        },

        data() {
            return {
                localUsers: [...this.users],
                currentMessages: [],
                currentMessagesPaginationData: null,
                currentUser: null,
                search: '',
                openChatList: false, // for small screen
                searchUserList: [],

                loading: false,
                loadingMoreMessages: false,
            };
        },

        computed: {
            chatUserList() {
                if (this.search.length) return this.searchUserList;
                return this.localUsers;
            },
        },

        watch: {
            search: {
                // eslint-disable-next-line func-names
                handler: throttle(function () {
                    this.fetchSearchResults();
                }, 1500),
            },
        },

        created() {
            console.log('Pusher is called');
            // eslint-disable-next-line no-undef
            Echo.private(`App.Models.User.${this.$page.props.user.id}`).listen('MessageSent', (e) => {
                console.log('Message received from pusher:', e);
                if (this.currentUser && this.currentUser.id === e.sender.id) {
                    this.currentMessages.push({
                        sender: e.sender,
                        recipient: e.recipient,
                        message: e.message.message,
                    });
                    this.fetchCurrentUserMessaged();
                }
            });
        },

        mounted() {
            if (this.localUsers.length) this.changeCurrentUser(this.localUsers[0]);
            this.fetchMoreCurrentUserMessaged().then(() => {
                document.getElementById('chat-text-box').focus();
            });

            this.$nextTick(() => {
                const messageBlock = this.$refs.chatmessagesref;
                if (messageBlock) {
                    messageBlock.scrollTop = messageBlock.scrollHeight;
                }
            });
        },

        methods: {
            addMessage(recipient, sender, message) {
                this.currentMessages.push({
                    recipient,
                    sender,
                    message,
                    created_at: new Date(),
                });
                this.$nextTick(() => {
                    const messageBlock = this.$refs.chatmessagesref;
                    if (messageBlock) {
                        messageBlock.scrollTop = messageBlock.scrollHeight;
                    }
                });

                this.$axios.post(route('chat.store'), {
                    recipient_id: recipient.id,
                    message,
                });
            },

            async changeCurrentUser(user) {
                if (!this.localUsers.some((u) => u.id === user.id)) this.localUsers.push(user);
                this.currentUser = user;
                try {
                    this.fetchCurrentUserMessaged();
                } catch (error) {
                    console.log(error);
                }
            },

            isCurrentRecipient(user) {
                if (this.currentUser && this.currentUser.id === user.id) {
                    return true;
                }
                return false;
            },

            closeChatList(user) {
                this.openChatList = false;
                if (user) {
                    this.changeCurrentUser(user);
                }
            },

            async fetchSearchResults() {
                if (this.search) {
                    try {
                        this.loading = true;
                        const {
                            data: { users },
                        } = await this.$axios.get(route('chat.search.users', this.search));
                        this.searchUserList = users;
                    } catch (error) {
                        console.log(error);
                    } finally {
                        this.loading = false;
                    }
                }
            },

            fetchCurrentUserMessaged() {
                const messageBlock = document.getElementById('chat-messages');
                this.$axios.get(route('chat.fetchMessages', { userId: this.currentUser.id })).then((res) => {
                    this.currentMessagesPaginationData = res.data.messages;
                    this.currentMessages = res.data.messages.data.sort((a, b) => {
                        const dateA = new Date(a.created_at);
                        const dateB = new Date(b.created_at);
                        return dateA - dateB;
                    });
                });
                // .then(() => {
                //     messageBlock.scrollTo(0, 400);
                // });
                this.$nextTick(() => {
                    const messageBlock = this.$refs.chatmessagesref;
                    if (messageBlock) {
                        messageBlock.scrollTop = messageBlock.scrollHeight;
                    }
                });
            },

            // this function does not fetch more messages on mount
            // instead it sets up a event on scroll of message box to fetch more messages.
            async fetchMoreCurrentUserMessaged() {
                const messageBlock = document.getElementById('chat-messages');
                messageBlock.onscroll = () => {
                    if (
                        messageBlock.scrollTop === 0 &&
                        !this.loadingMoreMessages &&
                        this.currentMessages.length !== this.currentMessagesPaginationData.total
                    ) {
                        this.loadingMoreMessages = true;
                        const scrollPositionMessage = this.currentMessages[0];
                        this.$axios
                            .get(
                                route('chat.fetchMessages', {
                                    userId: this.currentUser.id,
                                    page: this.currentMessagesPaginationData.current_page + 1,
                                }),
                            )
                            .then((res) => {
                                this.currentMessagesPaginationData = res.data.messages;
                                this.currentMessages = [...res.data.messages.data, ...this.currentMessages].sort(
                                    (a, b) => {
                                        const dateA = new Date(a.created_at);
                                        const dateB = new Date(b.created_at);
                                        return dateA - dateB;
                                    },
                                );
                                this.loadingMoreMessages = false;

                                const target = document.getElementById(`message-${scrollPositionMessage.id}`);
                                target.parentNode.scrollTop = target.offsetTop - 110;
                            })
                            .catch(() => {
                                this.loadingMoreMessages = false;
                                console.log('Something went wrong while fetching data.');
                            });
                    }
                };
            },
        },
    };
</script>
